import React, { useRef, useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';

interface CodeEditorProps {
  language: 'python' | 'java' | 'cpp' | 'c' | 'javascript';
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  height?: string;
  theme?: 'vs-dark' | 'light' | 'hc-black';
  placeholder?: string;
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  language,
  value,
  onChange,
  readOnly = false,
  height = '400px',
  theme,
  placeholder = 'Write your code here...'
}) => {
  const editorRef = useRef<any>(null);
  const [currentTheme, setCurrentTheme] = useState<'vs-dark' | 'light'>('vs-dark');

  // Detect system dark mode preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateTheme = (e: MediaQueryListEvent | MediaQueryList) => {
      setCurrentTheme(e.matches ? 'vs-dark' : 'light');
    };

    updateTheme(mediaQuery);
    mediaQuery.addEventListener('change', updateTheme);

    return () => mediaQuery.removeEventListener('change', updateTheme);
  }, []);

  // Use provided theme or fall back to system preference
  const editorTheme = theme || currentTheme;

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;

    // Set up language-specific configurations
    if (language === 'python') {
      // Check if Python language support is available before configuring
      if (monaco.languages.python && monaco.languages.python.pythonDefaults) {
        monaco.languages.python.pythonDefaults.setDiagnosticsOptions({
          lint: {
            enabled: true,
          },
        });
      }
    }

    // Add placeholder text
    if (!value.trim()) {
      editor.setValue(placeholder);
      editor.getModel()?.onDidChangeContent(() => {
        if (editor.getValue() === placeholder) {
          editor.setValue('');
        }
      });
    }
  };

  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      onChange(value);
    }
  };

  const getLanguageId = (language: string) => {
    switch (language) {
      case 'python':
        return 'python';
      case 'java':
        return 'java';
      case 'cpp':
        return 'cpp';
      case 'c':
        return 'c';
      case 'javascript':
        return 'javascript';
      default:
        return 'plaintext';
    }
  };

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      <Editor
        height={height}
        language={getLanguageId(language)}
        value={value}
        onChange={handleEditorChange}
        theme={editorTheme}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          roundedSelection: false,
          scrollBeyondLastLine: false,
          readOnly: readOnly,
          automaticLayout: true,
          wordWrap: 'on',
          folding: true,
          lineDecorationsWidth: 10,
          lineNumbersMinChars: 3,
          glyphMargin: false,
          useTabStops: false,
          tabSize: 2,
          insertSpaces: true,
          detectIndentation: true,
          trimAutoWhitespace: true,
          largeFileOptimizations: true,
          suggest: {
            showKeywords: true,
            showSnippets: true,
            showClasses: true,
            showFunctions: true,
            showVariables: true,
            showModules: true,
            showProperties: true,
            showEvents: true,
            showOperators: true,
            showUnits: true,
            showValues: true,
            showConstants: true,
            showEnums: true,
            showEnumMembers: true,
            showColors: true,
            showFiles: true,
            showReferences: true,
            showFolders: true,
            showTypeParameters: true,
            showWords: true,
          },
        }}
        onMount={handleEditorDidMount}
      />
    </div>
  );
};

export default CodeEditor;